export type ResourceStatus = "draft" | "published";

export type ResourceCategory =
  | "Compliance foundations"
  | "FTC guidance"
  | "Affiliate monitoring"
  | "Regulatory Library"
  | "Templates & Checklists"
  | "Insights"
  | "Case Studies";

export type ResourceContentType =
  | "Guide"
  | "Regulatory Library"
  | "Templates & Checklists"
  | "Insights"
  | "Case Study";

export interface ResourceLink {
  href: string;
  label: string;
  description?: string;
}

export interface ResourceSource {
  title: string;
  publisher: string;
  url: string;
  note?: string;
}

export interface ResourceExample {
  label: string;
  title: string;
  before: string;
  after: string;
  note?: string;
}

export interface ResourceSubsection {
  id: string;
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface ResourceSection {
  id: string;
  heading: string;
  paragraphs: string[];
  bullets?: string[];
  subsections?: ResourceSubsection[];
  example?: ResourceExample;
}

interface ResourceCommonFields {
  slug: string;
  status: ResourceStatus;
  title: string;
  description: string;
  category: ResourceCategory;
  author: string;
  reviewedAt: string;
  heroImage: string;
  seo: {
    title: string;
    description: string;
  };
  canonicalUrl: string;
  /**
   * Minutes shown as "X min read" everywhere this resource appears (hero and
   * list card). Keep this in sync with the number hardcoded in the article's
   * own hero component -- it is not derived from word count.
   */
  readTimeMinutes: number;
  directAnswer: string;
  sections: ResourceSection[];
  primarySources: ResourceSource[];
  relatedProduct: ResourceLink;
  relatedResources: ResourceLink[];
  finalCta: {
    title: string;
    body: string;
    href: string;
    label: string;
  };
  disclaimer?: string;
}

/**
 * Regulatory Library entries summarize a primary regulatory source in plain
 * language. `relatedProduct` (already on ResourceCommonFields) doubles as the
 * "related PinkSpider capability" field called for in the entry shape.
 */
export interface RegulatoryLibraryFields {
  regulatoryBody: string;
  jurisdiction: string;
  sourceUrl: string;
  whatTheSourceSays: string;
  whatToWatchFor: string;
  creatorImplications: string;
  operationalReviewQuestions: string[];
}

/**
 * Templates & Checklists entries are practical, usable tools. They can be
 * web-based (rendered directly on the page) or a real downloadable asset --
 * never a gated placeholder that links to content that doesn't exist yet.
 */
export interface TemplateChecklistFields {
  toolFormat: "web" | "download";
  toolSummary: string;
  toolItems: string[];
  downloadUrl?: string;
}

type ResourceContentTypeFields =
  | { contentType: "Guide" }
  | ({ contentType: "Regulatory Library" } & RegulatoryLibraryFields)
  | ({ contentType: "Templates & Checklists" } & TemplateChecklistFields)
  | { contentType: "Insights" }
  | { contentType: "Case Study" };

type ResourceBase = ResourceCommonFields & ResourceContentTypeFields;

export type PublishedResource = ResourceBase & {
  status: "published";
  publishedAt: string;
};

export type DraftResource = ResourceBase & {
  status: "draft";
  publishedAt?: string;
};

export type Resource = PublishedResource | DraftResource;

export interface ResourceRoute {
  path: string;
  status: 200;
  indexation: "index";
  title: string;
  description: string;
  changefreq: "weekly";
  priority: string;
  content: {
    h1: string;
    paragraphs: string[];
    links: ResourceLink[];
  };
  resource: PublishedResource;
}

export const RESOURCE_SITE_URL = "https://pinkspider.ai";

/**
 * Display label shown in a resource article's header for each content type.
 * Keep these consistent with how the type is described elsewhere on the page
 * (e.g. the Regulatory Library and Template metadata blocks).
 */
export const RESOURCE_CONTENT_TYPE_LABELS: Record<ResourceContentType, string> = {
  Guide: "Practical Guide",
  "Regulatory Library": "Regulatory Library",
  "Templates & Checklists": "Template & Checklist",
  Insights: "Insight",
  "Case Study": "Case Study",
};
const REGULATORY_DISCLAIMER =
  "Informational only, not legal advice. Rules and guidance can change, and the right review depends on your facts, audience, claims, contracts, and jurisdictions. Consult qualified counsel for advice about your program.";

const FTC_DISCLOSURES_101: ResourceSource = {
  title: "Disclosures 101 for Social Media Influencers",
  publisher: "Federal Trade Commission",
  url: "https://www.ftc.gov/business-guidance/resources/disclosures-101-social-media-influencers",
  note: "Practical FTC guidance on when and how influencers should disclose material connections.",
};

const FTC_ENDORSEMENT_GUIDES: ResourceSource = {
  title: "Guides Concerning the Use of Endorsements and Testimonials in Advertising",
  publisher: "Federal Trade Commission",
  url: "https://www.ftc.gov/legal-library/browse/rules/endorsement-guides",
  note: "The FTC's rules and guidance for endorsements and testimonials in advertising.",
};

const FTC_COM_DISCLOSURES: ResourceSource = {
  title: ".com Disclosures: How to Make Effective Disclosures in Digital Advertising",
  publisher: "Federal Trade Commission",
  url: "https://www.ftc.gov/business-guidance/resources/com-disclosures-how-make-effective-disclosures-digital-advertising",
  note: "FTC guidance on making disclosures clear and conspicuous in digital formats.",
};

const FTC_REVIEWS_TESTIMONIALS_QA: ResourceSource = {
  title: "The Consumer Reviews and Testimonials Rule: Questions and Answers",
  publisher: "Federal Trade Commission",
  url: "https://www.ftc.gov/business-guidance/resources/consumer-reviews-testimonials-rule-questions-answers",
  note: "FTC staff Q&A explaining the scope and provisions of the rule.",
};

const FTC_REVIEWS_TESTIMONIALS_FINAL_RULE: ResourceSource = {
  title: "Trade Regulation Rule on the Use of Consumer Reviews and Testimonials (16 CFR Part 465)",
  publisher: "Federal Trade Commission",
  url: "https://www.ftc.gov/legal-library/browse/federal-register-notices/16-cfr-part-465-trade-regulation-rule-use-consumer-reviews-testimonials-final-rule",
  note: "The final rule text as published in the Federal Register.",
};

const FTC_GREEN_GUIDES_OVERVIEW: ResourceSource = {
  title: "Green Guides",
  publisher: "Federal Trade Commission",
  url: "https://www.ftc.gov/news-events/topics/truth-advertising/green-guides",
  note: "FTC overview of the Green Guides and their purpose.",
};

const FTC_GREEN_GUIDES_SUMMARY: ResourceSource = {
  title: "Environmental Claims: Summary of the Green Guides",
  publisher: "Federal Trade Commission",
  url: "https://www.ftc.gov/business-guidance/resources/environmental-claims-summary-green-guides",
  note: "FTC staff summary of the general principles and claim-by-claim guidance in the Green Guides.",
};

const FTC_COPPA_FAQ: ResourceSource = {
  title: "Complying with COPPA: Frequently Asked Questions",
  publisher: "Federal Trade Commission",
  url: "https://www.ftc.gov/business-guidance/resources/complying-coppa-frequently-asked-questions",
  note: "FTC staff guidance explaining who the COPPA Rule covers and what it requires.",
};
const FTC_COPPA_RULE: ResourceSource = {
  title: "Children's Online Privacy Protection Rule (\"COPPA\")",
  publisher: "Federal Trade Commission",
  url: "https://www.ftc.gov/legal-library/browse/rules/childrens-online-privacy-protection-rule-coppa",
  note: "The COPPA Rule text (16 CFR Part 312) as maintained in the FTC's legal library.",
};
const publishedOn = "2026-08-24";
const brandLiabilityPublishedOn = "2026-08-19";
const onboardingChecklistPublishedOn = "2026-08-31";

const NAD_ABOUT: ResourceSource = {
  title: "About NAD",
  publisher: "BBB National Programs",
  url: "https://bbbprograms.org/programs/all-programs/national-advertising-division",
  note: "Overview of the National Advertising Division, the self-regulatory forum that reviews national advertising claims.",
};

export const RESOURCES: Resource[] = [
  {
    slug: "who-is-liable-for-influencer-marketing-claims",
    status: "published",
    contentType: "Guide",
    title: "Who's Liable for Influencer Marketing Claims?",
    description:
      "Why brands carry the most risk when a creator post goes wrong, where exposure comes from beyond the FTC, and what reasonable monitoring looks like at 400 creators.",
    category: "FTC guidance",
    author: "PinkSpider editorial team",
    publishedAt: brandLiabilityPublishedOn,
    reviewedAt: brandLiabilityPublishedOn,
    heroImage: "/brand-liability-hero.jpg",
    seo: {
      title: "Who's Liable for Influencer Marketing Claims? | PinkSpider",
      description:
        "The advertiser is responsible for a creator's endorsement, including a disclosure it never saw. What the 2025 class actions changed, what NAD does first, and what a contract can't do.",
    },
    canonicalUrl: `${RESOURCE_SITE_URL}/resources/who-is-liable-for-influencer-marketing-claims`,
    readTimeMinutes: 4,
    directAnswer:
      "The FTC treats your brand as the advertiser behind every creator post. That means you answer for what a creator says about your product, even when you never saw the post before it went live. Creators can be held responsible too, but that doesn't take your brand off the hook. This is one reason TikTok Shop makes brands nervous. Affiliates can post about your product without ever touching your approval process, and, as recent class actions have shown, your brand still answers for it. Luckily PinkSpider provides a solution. A contract can set the disclosure standard and create a takedown right, but it cannot move FTC liability off the advertiser, bind consumers who never signed it, or substitute for monitoring the brand actually performs.",
    sections: [
      {
        id: "advertiser",
        heading: "There is no split",
        paragraphs: [
          "Most brands come to this question hoping for a split: how much of the disclosure obligation sits with the brand, and how much sits with the creator. There isn't one. The FTC's Endorsement Guides treat a paid creator post as the brand's own advertising, so an inadequate disclosure is a problem with that advertising. Creators can be named too, and plaintiffs increasingly name them, but that doesn't reduce what the brand carries.",
          "This is why \"we didn't know about that post\" describes the problem rather than answers it. A program with 400 creators and no record of what went live isn't lower risk than one with 40, it's the same risk with less visibility.",
        ],
      },
      {
        id: "oversight",
        heading: "Oversight, not just paperwork",
        paragraphs: [
          "Regulators, courts, and platforms tend to look for the same thing: a program that's actively managed, not just documented. That generally means training the people the brand pays, giving disclosure instructions specific to the format creators are posting in, monitoring what actually publishes, and acting when something is wrong. A signed acknowledgment shows the brand asked; it doesn't show the brand was watching.",
        ],
        bullets: [
          "Reads as paperwork: a contract clause, a one-time onboarding deck, spot-checking a handful of posts, escalation by inbox thread.",
          "Reads as oversight: format-specific disclosure guidance per deliverable, review before publish, continuous detection of live affiliate posts, dated findings and remediations.",
        ],
      },
      {
        id: "classactions",
        heading: "The 2025 class actions changed the math",
        paragraphs: [
          "2025 introduced a private plaintiffs' bar to the disclosure fact pattern, using state consumer-protection statutes and a price-premium damages theory: undisclosed paid posts presented as organic enthusiasm, and consumers who say they paid more than they otherwise would have.",
          "Two of the three major 2025 filings have been routed toward individual arbitration rather than class treatment, which limits the class-damages threat but does not remove it. Pop v. LuliFama.com LLC affirmed a dismissal on pleading grounds, not on the merits of whether undisclosed endorsements are lawful.",
        ],
        bullets: [
          "Dubreu v. Celsius, 5:25-cv-00180 (C.D. Cal.), filed Jan 22, 2025, pending.",
          "Negreanu v. Revolve, 2:25-cv-03186 (C.D. Cal.), filed Apr 11, 2025, class claims struck and compelled to arbitration.",
          "Bengoechea v. Shein, 1:25-cv-01402 (N.D. Ill.), filed Feb 10, 2025, arbitration sought.",
          "Statutory maximum civil penalty is $53,088 per violation under FTC Act \u00a7\u00a7 5(l) and 5(m)(1), an outer bound rather than an expected value.",
        ],
      },
      {
        id: "nad",
        heading: "NAD gets there first",
        paragraphs: [
          "The forum most likely to reach a program first is not a regulator. The National Advertising Division reviews national advertising claims and refers non-compliant advertisers to the FTC, and was active through 2025 on influencer and ambassador disclosure, including a January 2025 decision on Revolve's gifting-program guidelines.",
          "A competitor can bring a case, so the trigger is commercial rather than statistical, and the timeline runs in weeks rather than the months a regulatory inquiry takes.",
        ],
      },
      {
        id: "contracts",
        heading: "What a contract can and cannot do",
        paragraphs: [
          "Indemnity is worth having. It is not a transfer of liability. Private allocation between a brand and a creator binds those two parties; it does not bind the Commission, a state attorney general, or a class of consumers.",
        ],
        bullets: [
          "A contract can: set the disclosure standard per format, require pre-publish submission, create a fast takedown right, and recover cost after the fact.",
          "A contract cannot: move FTC liability off the advertiser, bind consumers who never signed it, substitute for actual monitoring, or answer what the brand did when it found out.",
        ],
      },
      {
        id: "monitoring",
        heading: "What reasonable monitoring looks like at 400 creators",
        paragraphs: [
          "Reasonableness scales with the program. At twenty creators, a person reading every post is reasonable. At four hundred, it is an aspiration rather than a control. A defensible program has coverage stated as a number, a written standard that does not move silently, findings tied to where they were observed, and a dated disposition on everything flagged.",
        ],
      },
      {
        id: "documentation",
        heading: "The documentation that survives an inquiry",
        paragraphs: [
          "Assume a request for the last twelve months. Most programs can produce the creator list and the instructions given, but rarely the full set of published posts, the review each one received, what happened after a violation, or a version history of the compliance standard itself. Those last four are where positions are won or lost.",
        ],
      },
      {
        id: "record",
        heading: "What PinkSpider puts on the record",
        paragraphs: [
          "Complete review and decision history on every piece of content, AI findings and human findings that coexist rather than overwrite each other, and a Score Management log that keeps every change to the standard, who made it, and why.",
        ],
      },
    ],
    primarySources: [
      FTC_ENDORSEMENT_GUIDES,
      FTC_DISCLOSURES_101,
      NAD_ABOUT,
    ],
    relatedProduct: {
      href: "/platform",
      label: "See how PinkSpider documents review, monitoring, and remediation",
      description:
        "A complete, exportable record of what was scored, flagged, decided, and changed, so the next inquiry is answered from the artifact.",
    },
    relatedResources: [
      {
        href: "/resources/creator-marketing-compliance-guide",
        label: "The Creator Marketing Compliance Guide",
      },
      {
        href: "/resources/escalation-playbook",
        label: "Escalation Playbook: Process for Non-Compliant Live Posts",
      },
    ],
    finalCta: {
      title: "Start with what's already live",
      body:
        "See how PinkSpider can support the review, monitoring, and documentation work behind your program.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
    disclaimer: REGULATORY_DISCLAIMER,
  },
  {
    slug: "creator-marketing-compliance-guide",
    status: "published",
    contentType: "Guide",
    title: "The Creator Marketing Compliance Guide",
    description:
      "The flagship guide to creator marketing compliance: who is liable, the three regulatory regimes that matter, how to write a disclosure that actually works, and how to catch problems after a post goes live.",
    category: "Compliance foundations",
    author: "PinkSpider editorial team",
    publishedAt: publishedOn,
    reviewedAt: publishedOn,
    heroImage: "/compliance-guide-hero.jpg",
    seo: {
      title: "The Creator Marketing Compliance Guide | PinkSpider",
      description:
        "A complete, source-backed guide to creator marketing compliance: brand liability, FTC disclosures, platform rules, industry-specific claims, and post-publication monitoring.",
    },
    canonicalUrl: `${RESOURCE_SITE_URL}/resources/creator-marketing-compliance-guide`,
    readTimeMinutes: 3,
    directAnswer:
      "Brands, not just creators, can be held responsible for undisclosed material connections and unsupported claims in creator content. A workable compliance program covers three regimes at once (FTC disclosure rules, platform policies, and category-specific advertising law), gives creators disclosure language that survives editing, and monitors what actually goes live rather than only what was approved in a brief.",
    sections: [
      {
        id: "who-is-liable",
        heading: "Who is actually liable when a post goes wrong",
        paragraphs: [
          "The FTC has been direct on this point: both the brand and the creator can be held responsible for a misleading endorsement or a missing disclosure. A contract that puts the compliance burden entirely on the creator does not remove the brand's own exposure.",
          "That shared liability is why a brand-side review step matters even when creators are contractually required to disclose. The brand is the party with the most to lose and the most ability to catch a problem before it reaches an audience.",
        ],
        bullets: [
          "A brand can be liable for a creator's undisclosed material connection, even if the creator wrote the caption.",
          "A brand can be liable for a claim it fed the creator through talking points, even if the creator delivered it in their own words.",
          "Indemnification clauses shift cost between the parties after the fact; they do not prevent regulatory action against the brand.",
        ],
      },
      {
        id: "three-regimes",
        heading: "Three regulatory regimes, reviewed at the same time",
        paragraphs: [
          "Creator content sits inside three overlapping systems: FTC endorsement and disclosure law, the platform's own advertising and branded-content policies, and category-specific advertising rules for regulated products such as supplements, alcohol, finance, or gambling.",
          "Treating these as one review, rather than three separate approvals, is what keeps a program from missing the regime that a specific post actually triggers.",
        ],
        bullets: [
          "FTC: material connections, clear and conspicuous disclosure, truthful and substantiated claims.",
          "Platform policy: branded-content tools, paid-partnership labels, category restrictions, and prohibited claims by platform.",
          "Category law: the rules that apply to the product itself, such as supplement health claims, alcohol marketing restrictions, financial promotion rules, or gambling advertising limits.",
        ],
      },
      {
        id: "material-connections",
        heading: "Material connections and what counts as a disclosure",
        paragraphs: [
          "A material connection is any relationship that could affect how an audience weighs an endorsement: payment, free or discounted product, an affiliate commission, an employment relationship, or a personal or family relationship with the brand.",
          "An adequate disclosure is unambiguous, in the same language as the content, placed before the audience has to take action to see it, and repeated across every format where the endorsement appears. An inadequate disclosure buries the relationship in a bio link, uses jargon the audience has to decode, or appears only in a format the audience is unlikely to reach, such as the end of a long video.",
        ],
        example: {
          label: "Practical example",
          title: "The same claim, disclosed two different ways",
          before:
            "\"Obsessed with how my skin looks after two weeks #skincare #glowup\" with a link in bio and a #partner tag buried in a block of 20 hashtags.",
          after:
            "\"Brand sent me this to try (#ad): here's my honest two-week results.\" stated in the first line of the caption and repeated verbally in the video.",
          note: "The right treatment depends on the platform, format, and facts; this illustrates the difference between an easy-to-miss and an easy-to-notice disclosure.",
        },
      },
      {
        id: "platform-and-claims",
        heading: "Platform rules and claim substitution",
        paragraphs: [
          "Each major platform layers its own advertising policy on top of FTC and category law: required paid-partnership tools, restricted categories (such as gambling or alcohol in some regions), and platform-specific prohibited claims.",
          "When a claim cannot be substantiated or is restricted by category law, the fix is usually a claim swap rather than a flat rejection: replacing an unsupported outcome claim with a supportable statement about experience, ingredients, or process keeps the content authentic while removing the risk.",
        ],
        bullets: [
          "Review the platform's branded-content and advertising policy for the specific category before a campaign launches, not after a complaint.",
          "Keep a short list of pre-approved claim swaps so creators have supportable alternatives instead of guessing.",
          "Re-review evergreen or repurposed content when it moves to a new platform, since the same post can violate one platform's policy and not another's.",
        ],
      },
      {
        id: "find-your-regulator",
        heading: "Find the regulator and category rules that apply to you",
        paragraphs: [
          "Beyond the FTC, several categories carry their own regulator and rulebook: FINRA and the SEC for financial promotions, the TTB for alcohol marketing, state gaming commissions for gambling advertising, and FDA guidance for health and supplement claims.",
          "A brand operating in a regulated category should treat that regulator's guidance as a primary source alongside FTC disclosure rules, not as a replacement for them.",
        ],
        bullets: [
          "Health, supplements & wellness: FDA and FTC substantiation standards for health and efficacy claims.",
          "Finance & fintech: FINRA and SEC rules on testimonials, performance claims, and required disclosures.",
          "Alcohol & spirits: TTB advertising rules and state-level restrictions on health claims and audience targeting.",
          "Gambling & gaming: state gaming commission advertising rules and responsible-gambling disclosure requirements.",
        ],
      },
      {
        id: "monitor-after-publication",
        heading: "Monitor what actually goes live",
        paragraphs: [
          "A brief can be approved and the published post can still differ from it: a creator edits a caption after approval, a disclosure gets cut from a repost, or a comment adds an unapproved claim. Pre-publication review alone cannot catch that.",
          "A monitoring layer should track the live URL, capture the disclosure and claims as published, and route anything that drifted from the approved version to a reviewer with the evidence attached, not just a flag.",
        ],
        bullets: [
          "Check the disclosure and claims on the published post, not only the version submitted for approval.",
          "Re-check evergreen content periodically, since platform policy and product claims can change after a post goes live.",
          "Give reviewers the exact caption, frame, or timestamp that triggered a flag so follow-up with the creator is specific and fast.",
        ],
      },
    ],
    primarySources: [
      FTC_DISCLOSURES_101,
      FTC_ENDORSEMENT_GUIDES,
      FTC_COM_DISCLOSURES,
    ],
    relatedProduct: {
      href: "/platform",
      label: "See the creator marketing compliance platform",
      description:
        "Connect review, monitoring, remediation, training, and program insight in one workspace.",
    },
    relatedResources: [
      {
        href: "/resources/who-is-liable-for-influencer-marketing-claims",
        label: "Who's Liable for Influencer Marketing Claims?",
      },
      {
        href: "/resources/monthly-creator-compliance-review",
        label: "The Monthly Creator Compliance Review",
      },
    ],
    finalCta: {
      title: "Make the next review clearer",
      body:
        "See how PinkSpider can support the review, monitoring, and creator enablement work behind your program.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
    disclaimer: REGULATORY_DISCLAIMER,
  },
  {
    slug: "ftc-influencer-disclosures",
    status: "published",
    contentType: "Guide",
    title: "FTC Influencer Disclosures: What Brands Need to Know",
    description:
      "A source-backed guide to the brand-side workflow for reviewing influencer disclosures across captions, video, live content, and affiliate posts.",
    category: "FTC guidance",
    author: "PinkSpider editorial team",
    publishedAt: publishedOn,
    reviewedAt: publishedOn,
    heroImage: "/hero-ftc-influencer-disclosures.jpg",
    seo: {
      title: "FTC Influencer Disclosures: What Brands Need to Know | PinkSpider",
      description:
        "Understand the FTC disclosure workflow for brands: material connections, clear placement, video and live content, review evidence, and creator follow-up.",
    },
    canonicalUrl: `${RESOURCE_SITE_URL}/resources/ftc-influencer-disclosures`,
    readTimeMinutes: 4,
    directAnswer:
      "Brands should review whether an influencer has a material connection to the brand and whether the disclosure is clear, easy to notice, and understandable in the content format where the endorsement appears. The brand workflow should check the disclosure in context, give the creator a specific correction, and retain the reviewed version and decision.",
    sections: [
      {
        id: "start-with-material-connection",
        heading: "Start with the material connection",
        paragraphs: [
          "The first question is whether the audience would understand the relationship between the endorser and the advertiser without being told. Payment, free or discounted products, affiliate commissions, employment, or another benefit can change how an endorsement should be evaluated.",
          "The FTC's Disclosures 101 guide is a useful starting point for practical examples. A brand's intake form should capture the relationship before the content reaches final review, because the same words can carry different context when the audience does not know the connection.",
        ],
        bullets: [
          "Record the relationship for each creator and campaign, not only in a contract folder.",
          "Check whether a post includes multiple relationships or calls to action.",
          "Do not assume that a platform's paid-partnership label answers every disclosure question.",
          "Escalate unusual arrangements and unclear facts to the appropriate human reviewer.",
        ],
      },
      {
        id: "review-disclosure-in-context",
        heading: "Review the disclosure in the format people see",
        paragraphs: [
          "A disclosure should be evaluated where the endorsement happens. In a caption, check whether it is easy to notice before a reader needs to expand the post. In video, check spoken and on-screen treatment, timing, contrast, and whether the disclosure remains understandable when a viewer only watches part of the clip. In a live stream, plan for repetition and moderation because people join at different times.",
          "The FTC's .com Disclosures guidance emphasizes that disclosures should be clear and conspicuous in digital advertising. A reviewer should inspect the actual rendered asset rather than relying on a brief or a text-only export.",
        ],
        example: {
          label: "Practical example",
          title: "A disclosure that is technically present but easy to miss",
          before:
            "A video includes #ad in a long caption while the spoken endorsement begins immediately and the on-screen text is low contrast.",
          after:
            "The reviewer asks for a disclosure treatment that is noticeable in the video context and checks the caption placement separately before approval.",
          note: "The right treatment depends on the format, facts, and applicable guidance; this is a review pattern, not a universal prescription.",
        },
        subsections: [
          {
            id: "format-specific-checks",
            heading: "Use format-specific checks",
            paragraphs: [
              "Your checklist can stay short while still being format-aware. Ask where the disclosure appears, when it appears, how a typical viewer encounters it, and whether the words are understandable without decoding platform shorthand.",
            ],
            bullets: [
              "Caption: placement, visibility before expansion, and plain language.",
              "Video: spoken disclosure, on-screen disclosure, timing, and legibility.",
              "Stories or short-lived formats: each frame that contains an endorsement or call to action.",
              "Live content: opening disclosure, repetition, pinned context, and moderator readiness.",
            ],
          },
        ],
      },
      {
        id: "give-a-specific-correction",
        heading: "Give creators a specific correction",
        paragraphs: [
          "When a disclosure needs work, tell the creator the asset and location, what the reviewer observed, and what the next reviewable version should contain. Avoid turning a source-backed concern into a threatening or absolute message. The goal is to make the correction understandable and documentable.",
          "A good correction also protects the relationship. Creators can act faster when the brand gives one owner, one requested change, and one path to ask a question.",
        ],
        bullets: [
          "Quote or point to the relevant caption, spoken line, frame, or story card.",
          "Explain the material connection in the campaign context.",
          "Use examples as examples, not as a promise that a phrase always works.",
          "Record the revised version and the reviewer who made the final decision.",
        ],
      },
      {
        id: "monitor-after-approval",
        heading: "Monitor after approval and when the content changes",
        paragraphs: [
          "An approved draft is a point-in-time decision. Monitor the published version for edits, changed links, new comments or frames, and posts that were never submitted. Affiliate programs deserve particular attention because commission relationships and product links can create a long tail of content outside a campaign team's normal queue.",
          "Route a signal to a person with enough context to decide what to do. A monitoring tool can help find and organize evidence, but it should not be presented as a substitute for legal advice or human review.",
        ],
        bullets: [
          "Compare the published asset with the approved version when possible.",
          "Keep the observed text, timestamp, location, and outreach history together.",
          "Use recurring flags to improve creator onboarding and campaign briefs.",
          "Define an escalation route for issues that may need counsel or a platform-specific response.",
        ],
      },
      {
        id: "document-a-defensible-process",
        heading: "Document the process, not just the answer",
        paragraphs: [
          "A useful record shows what the team knew and reviewed at the time: the relationship, the asset version, the relevant source or internal rule, the decision, and any requested correction. That record helps teams learn from repeat issues and explain how a decision was made without claiming that the record guarantees a particular outcome.",
          "Review your source links periodically. Regulatory guidance and platform features change, so a resource library should show when an article was last reviewed and make its primary sources easy to inspect.",
        ],
      },
    ],
    primarySources: [
      FTC_DISCLOSURES_101,
      FTC_ENDORSEMENT_GUIDES,
      FTC_COM_DISCLOSURES,
    ],
    relatedProduct: {
      href: "/influencer-review",
      label: "Explore pre-publication influencer review",
      description:
        "Give reviewers and creators one place to inspect evidence and act on clear guidance.",
    },
    relatedResources: [
      {
        href: "/resources/who-is-liable-for-influencer-marketing-claims",
        label: "Who's Liable for Influencer Marketing Claims?",
      },
      {
        href: "/academy",
        label: "Turn recurring flags into Academy lessons",
      },
    ],
    finalCta: {
      title: "Bring your disclosure workflow into focus",
      body:
        "PinkSpider helps teams review content before publication, keep evidence with decisions, and give creators a clear next step.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
    disclaimer: REGULATORY_DISCLAIMER,
  },
  {
    slug: "affiliate-compliance-monitoring",
    status: "published",
    contentType: "Guide",
    title: "Affiliate Compliance Monitoring: What Brands Should Monitor",
    description:
      "A practical monitoring framework for affiliate and creator content, including disclosures, claims, links, evidence, triage, and follow-up.",
    category: "Affiliate monitoring",
    author: "PinkSpider editorial team",
    publishedAt: publishedOn,
    reviewedAt: publishedOn,
    heroImage: "/hero-affiliate-compliance-monitoring.jpg",
    seo: {
      title: "Affiliate Compliance Monitoring: What Brands Should Monitor | PinkSpider",
      description:
        "Build an affiliate compliance monitoring program around disclosures, claims, links, content changes, evidence, triage, and creator follow-up.",
    },
    canonicalUrl: `${RESOURCE_SITE_URL}/resources/affiliate-compliance-monitoring`,
    readTimeMinutes: 4,
    directAnswer:
      "Brands should monitor affiliate content for the presence and context of material-connection disclosures, product and performance claims, destination links, required brand language, content changes, and signs that a post bypassed review. Monitoring is most useful when each flag includes evidence, a priority, an owner, and a documented follow-up path.",
    sections: [
      {
        id: "why-affiliate-monitoring-is-different",
        heading: "Why affiliate monitoring is different",
        paragraphs: [
          "Affiliate content is distributed across many creators, platforms, pages, and publishing moments. A brand may have a contract and a brief, but it cannot assume every live placement will stay identical to the approved version or that every affiliate will use the same format.",
          "The monitoring goal is visibility and prioritization. It is not to label every detection as a violation. Signals need human context: what the relationship is, what the audience sees, what the brand asked for, and what action is proportionate.",
        ],
        bullets: [
          "The content may be created outside the brand's pre-approval queue.",
          "Affiliate links, discount codes, and landing pages can change after publication.",
          "The same creator may publish across formats with different disclosure constraints.",
          "A flag without the original evidence is difficult to verify or discuss constructively.",
        ],
      },
      {
        id: "define-monitoring-coverage",
        heading: "Define what your monitoring actually covers",
        paragraphs: [
          "Start with a coverage map. List the platforms, creator handles, campaign URLs, affiliate networks, product families, and content formats the team expects to see. Then mark what is discoverable, what requires a creator-submitted URL, and what needs a different operational check.",
          "Coverage boundaries should be visible to stakeholders. A clean report about a narrow set of URLs is not the same thing as evidence that every affiliate placement was reviewed.",
        ],
        bullets: [
          "Known creator and affiliate identities, including aliases where appropriate.",
          "Campaign, product, and destination-link relationships.",
          "Content types: captions, video transcripts, on-screen text, stories, comments, and landing pages.",
          "Scan cadence, observation timestamp, and the retention period for evidence.",
          "A clear owner for adding or correcting monitoring coverage.",
        ],
        example: {
          label: "Practical example",
          title: "A coverage gap hiding behind a healthy dashboard",
          before:
            "A team reports zero flags from a list of campaign URLs, but the list does not include creator posts that use a tracked discount code.",
          after:
            "The team joins code and link data to creator identities, labels the known coverage boundary, and treats newly found posts as a separate onboarding and review queue.",
          note: "Coverage planning improves visibility; it does not establish that all content is compliant.",
        },
      },
      {
        id: "monitor-the-right-signals",
        heading: "Monitor the signals that change a decision",
        paragraphs: [
          "A practical monitoring set focuses on signals that lead to a specific action. Disclosure context, claim language, destination-link changes, missing required terms, and content that differs from the approved version are usually more useful than a generic sentiment or keyword score.",
          "Use the FTC Endorsement Guides and Disclosures 101 as primary references for the disclosure and endorsement portions of the program. Pair those sources with the brand's own approved claims, category rules, contract terms, and escalation guidance.",
        ],
        subsections: [
          {
            id: "disclosure-and-relationship",
            heading: "Disclosure and relationship",
            paragraphs: [
              "Check whether the content makes the material connection understandable in context. A code, commission, free product, or other benefit can be an important part of the review record, but a detection alone does not answer how the disclosure should be handled.",
            ],
          },
          {
            id: "claims-and-brand-rules",
            heading: "Claims and brand rules",
            paragraphs: [
              "Compare product, performance, health, financial, or comparative language with the claims and evidence approved for that campaign. A phrase can require escalation even when it does not contain an obvious keyword.",
            ],
          },
          {
            id: "links-and-content-changes",
            heading: "Links and content changes",
            paragraphs: [
              "Check whether the destination, redirect, discount code, product availability, or required landing-page language changed. Keep the observed link and timestamp so a reviewer can reproduce the flag.",
            ],
          },
        ],
      },
      {
        id: "triage-with-evidence",
        heading: "Triage with evidence, priority, and ownership",
        paragraphs: [
          "Triage turns a large monitoring stream into a worklist. A useful item states what was found, where it was found, why it matters under the program's guidance, how confident the detection is, and who owns the next step. This gives a reviewer room to correct a false positive without losing the underlying evidence.",
          "Priorities should reflect context. A new claim in a risk-sensitive campaign may deserve faster attention than a known formatting issue in a low-risk post, but the specific thresholds belong to the brand and its advisers.",
        ],
        bullets: [
          "Attach the URL, excerpt, frame, transcript timestamp, or screenshot that supports the signal.",
          "Separate an observation from the interpretation a human reviewer still needs to make.",
          "Assign an owner and target response time for each priority level.",
          "Keep duplicate detections connected to one incident when they share a root cause.",
        ],
      },
      {
        id: "resolve-and-learn",
        heading: "Resolve the issue and improve the program",
        paragraphs: [
          "Outreach should be specific and proportionate. Tell the creator or affiliate what was observed, reference the relevant campaign guidance, ask for the next action, and provide a contact for questions. Preserve the original and corrected versions so the team can understand the change later.",
          "The most valuable monitoring output is often a program improvement: a clearer onboarding lesson, a better brief, a new approved claim, a corrected link process, or a decision about where pre-publication review is needed.",
        ],
        bullets: [
          "Close the loop with a resolution state such as corrected, escalated, not applicable, or awaiting response.",
          "Record the rationale rather than only the status label.",
          "Use repeated flags to update training and creator communications.",
          "Review source guidance and internal rules whenever the program changes.",
        ],
      },
    ],
    primarySources: [
      FTC_ENDORSEMENT_GUIDES,
      FTC_DISCLOSURES_101,
      FTC_COM_DISCLOSURES,
    ],
    relatedProduct: {
      href: "/affiliate-monitoring",
      label: "Explore affiliate monitoring",
      description:
        "Find post-publication signals, keep evidence attached, and prioritize constructive follow-up.",
    },
    relatedResources: [
      {
        href: "/resources/creator-marketing-compliance-guide",
        label: "The Creator Marketing Compliance Guide",
      },
      {
        href: "/resources/monthly-creator-compliance-review",
        label: "The Monthly Creator Compliance Review",
      },
      {
        href: "/solutions/creator-marketing",
        label: "See the creator marketing solution",
      },
    ],
    finalCta: {
      title: "See the content your approval queue misses",
      body:
        "PinkSpider helps teams monitor live creator and affiliate content, keep flags explainable, and move from signal to follow-up.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
    disclaimer: REGULATORY_DISCLAIMER,
  },
  {
    slug: "faster-marketing-review-workflow",
    status: "draft",
    contentType: "Guide",
    title: "How to Build a Faster Marketing Review Workflow",
    description:
      "Practical ways to speed up marketing review without skipping the checks that protect your brand, from intake to sign-off.",
    category: "Compliance foundations",
    author: "PinkSpider editorial team",
    publishedAt: publishedOn,
    reviewedAt: publishedOn,
    heroImage: "/hero-faster-marketing-review-workflow.jpg",
    seo: {
      title: "How to Build a Faster Marketing Review Workflow | PinkSpider",
      description:
        "Reduce marketing review turnaround time with better intake, risk-based routing, clear ownership, and fewer unnecessary review cycles.",
    },
    canonicalUrl: `${RESOURCE_SITE_URL}/resources/faster-marketing-review-workflow`,
    readTimeMinutes: 4,
    directAnswer:
      "Marketing review gets faster by removing avoidable delay, not by removing checks. The biggest wins usually come from better intake so reviewers get what they need the first time, risk-based routing so only higher-stakes content waits for deeper review, clear single-owner sign-off, and a shared view of where each item sits in the queue.",
    sections: [
      {
        id: "find-where-time-actually-goes",
        heading: "Find out where the time actually goes",
        paragraphs: [
          "Teams often assume review is slow because reviewers are slow, when the larger delay is upstream: content waits for someone to notice it was submitted, the submission is missing an asset the reviewer needs, or a decision sits in an inbox after it is made. Before changing the process, track how long content spends waiting versus how long it spends being actively reviewed.",
          "A short audit of your last few weeks of review requests usually surfaces the same two or three bottlenecks. Fix those first, because a new tool or extra headcount will not resolve a queue problem caused by unclear ownership.",
        ],
        bullets: [
          "Time from submission to a reviewer opening the item.",
          "Time from decision to the requester being notified.",
          "Number of review cycles per item before approval.",
          "Share of submissions missing information the reviewer needs.",
        ],
      },
      {
        id: "fix-intake-first",
        heading: "Fix intake before you touch anything else",
        paragraphs: [
          "A large share of review delay comes from incomplete submissions: a draft without the destination link, a video without a transcript, a campaign without its claims noted. Each missing piece turns into a question, and each question adds a full round trip to the timeline.",
          "A short, required intake form pays for itself quickly. Ask for exactly what a reviewer needs to make a decision the first time, and reject incomplete submissions automatically rather than letting a reviewer discover the gap partway through.",
        ],
        subsections: [
          {
            id: "keep-the-form-short",
            heading: "Keep the intake form short and specific",
            paragraphs: [
              "A ten-field form nobody fills in correctly is worse than a five-field form people actually complete. Ask for the content or link, the destination URL, the claims being made, the creator relationship, and the deadline. Add fields only when you can show they change a reviewer's decision.",
            ],
          },
        ],
      },
      {
        id: "route-by-risk-not-by-queue-order",
        heading: "Route by risk, not by queue order",
        paragraphs: [
          "First-in-first-out review treats a routine social caption the same as a claim in a regulated category, which means low-risk content waits behind high-risk content and vice versa. Sorting the queue by risk lets your reviewers spend their limited time where it matters and clear routine items quickly.",
          "Risk routing does not have to be complex. A short set of criteria (regulated category, new claim, new creator, paid amplification) is usually enough to split a queue into a fast lane and a deeper-review lane.",
        ],
        example: {
          label: "Practical example",
          title: "Splitting one queue into two lanes",
          before:
            "Every submission enters a single queue and is reviewed in the order it arrives, so a routine product photo can sit behind a complex claim for a full day.",
          after:
            "Submissions are tagged at intake; routine content clears a fast lane same-day, while flagged categories route to a reviewer with more context and a longer, planned turnaround.",
          note: "The right routing criteria depend on your brand's categories, risk tolerance, and team size.",
        },
      },
      {
        id: "assign-one-owner-per-decision",
        heading: "Assign one owner per decision",
        paragraphs: [
          "Review slows down when a decision needs sign-off from multiple people without a clear order, because each person assumes someone else will look at it first. Name a single owner who is responsible for the final decision on each item, even when other people contribute input.",
          "When more than one function genuinely needs to weigh in, such as legal, brand, and category expertise, set an order and a deadline for each reviewer's input, rather than sending the item to all three at once with no coordination.",
        ],
        bullets: [
          "One named owner for the final call on each submission.",
          "A defined order when multiple reviewers are required.",
          "A default outcome if a reviewer misses their window, agreed in advance.",
          "A visible status so the requester is not guessing where the item sits.",
        ],
      },
      {
        id: "reduce-unnecessary-cycles",
        heading: "Reduce unnecessary review cycles",
        paragraphs: [
          "Every extra round trip between a reviewer and a requester adds a full day or more in most organizations. Reduce cycles by giving specific, complete feedback the first time instead of flagging one issue, waiting for a fix, and then finding a second issue on the next pass.",
          "Track resubmission rates by reviewer and by requester. A pattern of repeat issues from the same source usually points to a training gap that a faster process cannot solve on its own; fixing the brief or the creator guidance prevents the review cycle from having to catch the same problem every time.",
        ],
      },
    ],
    primarySources: [
      FTC_ENDORSEMENT_GUIDES,
      FTC_COM_DISCLOSURES,
    ],
    relatedProduct: {
      href: "/solutions/marketing-review",
      label: "Build a shared marketing review workflow",
      description:
        "Connect intake, routing, and sign-off so review keeps pace with your publishing calendar.",
    },
    relatedResources: [
      {
        href: "/solutions/agencies",
        label: "See how agencies standardize review across clients",
      },
    ],
    finalCta: {
      title: "Clear the review queue without cutting corners",
      body:
        "See how PinkSpider helps teams route, review, and sign off on marketing content faster.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
    disclaimer: REGULATORY_DISCLAIMER,
  },
  {
    slug: "health-wellness-influencer-claims",
    status: "draft",
    contentType: "Guide",
    title: "Health & Wellness Influencer Claims: What Brands Should Review",
    description:
      "A review framework for health and wellness influencer claims, covering substantiation, disclosure context, and the questions to ask before approving a post.",
    category: "FTC guidance",
    author: "PinkSpider editorial team",
    publishedAt: publishedOn,
    reviewedAt: publishedOn,
    heroImage: "/hero-health-wellness-influencer-claims.jpg",
    seo: {
      title: "Health & Wellness Influencer Claims: What Brands Should Review | PinkSpider",
      description:
        "Learn what health and wellness brands should review in influencer content: claim types, substantiation questions, disclosure context, and escalation paths.",
    },
    canonicalUrl: `${RESOURCE_SITE_URL}/resources/health-wellness-influencer-claims`,
    readTimeMinutes: 4,
    directAnswer:
      "Health and wellness brands should review influencer claims for the specific outcome being promised, whether the brand has evidence to support that outcome, whether the language implies a medical or drug-like effect, and whether the material connection is clearly disclosed. Category-specific claims usually need a closer look than general lifestyle content, and unclear cases should go to a qualified reviewer rather than a generalist checklist.",
    sections: [
      {
        id: "why-this-category-needs-closer-review",
        heading: "Why health and wellness claims need closer review",
        paragraphs: [
          "Health and wellness content sits at the intersection of advertising rules and audience trust: an outcome claim about sleep, weight, pain, mood, or a medical condition can influence a real health decision, and creators sometimes describe personal experience in language that reads as a general promise. That gap between what a creator means and how a claim is likely to be understood is where brand review adds the most value.",
          "The FTC's Endorsement Guides make clear that endorsements must reflect the endorser's honest experience and that objective claims need adequate substantiation. A brand review process should apply that principle specifically to health-adjacent language, rather than assuming it only affects formal advertising copy.",
        ],
        bullets: [
          "Outcome claims: promises about weight, sleep, pain, energy, skin conditions, or mental health.",
          "Comparative claims: language suggesting a product works better than a medical treatment or alternative.",
          "Implied treatment claims: wording that suggests a product diagnoses, treats, or cures a condition.",
          "Personal-experience framing that a viewer may read as a general, expected result.",
        ],
      },
      {
        id: "ask-what-the-claim-actually-promises",
        heading: "Ask what the claim actually promises",
        paragraphs: [
          "Start by restating the claim in plain terms: what specific outcome does a viewer walk away expecting? \"This helped my skin feel smoother\" and \"this clears up acne\" are different claims with different substantiation needs, even if they appear in the same type of post. Reviewers should separate the product's approved marketing claims from anything a creator adds on their own.",
          "Pay attention to visuals and on-screen text as well as spoken or written claims. A before-and-after image, an on-screen number, or a caption emoji can communicate a claim just as clearly as a sentence, and it should be reviewed the same way.",
        ],
        subsections: [
          {
            id: "separate-approved-claims-from-added-claims",
            heading: "Separate approved claims from creator-added claims",
            paragraphs: [
              "A brief may list the claims a brand has approved for a campaign, but creators often add their own commentary. Review the final content against the approved claim list specifically, and treat any claim outside that list as a new item requiring its own review rather than an assumed extension of what was approved.",
            ],
          },
        ],
        example: {
          label: "Practical example",
          title: "The same product, two different claims",
          before:
            "A wellness brand approves general lifestyle content about a supplement, and a creator's video adds a specific claim that the product resolved a diagnosed sleep disorder.",
          after:
            "The reviewer flags the added claim as outside the approved scope, requests a revision that removes the specific medical claim, and documents the correction and the reason.",
          note: "Whether a specific claim requires substantiation or raises other issues depends on the product, the claim, and applicable rules; consult qualified counsel for a specific determination.",
        },
      },
      {
        id: "check-substantiation-and-escalate-when-unclear",
        heading: "Check substantiation, and escalate when it's unclear",
        paragraphs: [
          "A reviewer does not need to be a scientist to do a useful first pass: ask whether the brand has evidence supporting the specific claim being made, whether that evidence matches the product actually shown, and whether the claim goes further than what the evidence supports. If any answer is uncertain, that is the signal to escalate rather than approve.",
          "Build a short list of claim types that always route to a specialist reviewer or legal counsel, for example any claim referencing a diagnosed condition, a comparison to a drug or medical treatment, or a specific statistic. A generalist reviewer can handle disclosure and format checks, but category-specific substantiation calls usually need category-specific expertise.",
        ],
        bullets: [
          "Does the brand have evidence for this exact claim, not just a related one?",
          "Does the claim reference a diagnosed condition, treatment, or drug-like effect?",
          "Would a reasonable viewer read this as a guaranteed personal result?",
          "Is this claim type on the brand's escalation list for legal or medical review?",
        ],
      },
      {
        id: "review-disclosure-in-a-health-context",
        heading: "Review disclosure with health context in mind",
        paragraphs: [
          "Disclosure review for health and wellness content follows the same core question as any influencer content: is the material connection clear and understandable? The stakes of a viewer trusting the endorsement are often higher. Apply the FTC's Disclosures 101 guidance closely: the disclosure should be easy to notice, in language the audience understands, and placed where the claim itself appears, not buried in a hashtag list.",
          "Pay particular attention to video and short-form content, where a claim can appear on-screen for only a few seconds. If the outcome claim is not viewable long enough for a typical viewer to read, that is worth flagging alongside any disclosure concern.",
        ],
        bullets: [
          "Confirm the disclosure appears near the claim, not only at the end of a long caption.",
          "Check on-screen text timing and legibility in video content.",
          "Treat affiliate links and discount codes in health content with the same disclosure scrutiny as sponsored posts.",
          "Document the reviewed version so the team has a record if the content is questioned later.",
        ],
      },
      {
        id: "build-a-repeatable-review-note",
        heading: "Build a repeatable review note for this category",
        paragraphs: [
          "Keep a short, written record for each reviewed piece of health and wellness content: the claim as stated, the evidence checked, the disclosure assessment, the decision, and the reviewer. This is useful operationally even before any external question arises, because it helps the next reviewer see how similar claims were handled.",
          "Revisit your escalation list and claim examples periodically, since product formulations, approved marketing claims, and regulatory guidance can all change. A resource library entry is a starting point for review habits, not a substitute for a qualified reviewer's judgment on a specific claim.",
        ],
      },
    ],
    primarySources: [
      FTC_ENDORSEMENT_GUIDES,
      FTC_DISCLOSURES_101,
      FTC_COM_DISCLOSURES,
    ],
    relatedProduct: {
      href: "/industries/health-wellness",
      label: "See health & wellness compliance support",
      description:
        "Review creator content in a regulated category with claim and disclosure context in one place.",
    },
    relatedResources: [
      {
        href: "/resources/who-is-liable-for-influencer-marketing-claims",
        label: "Who's Liable for Influencer Marketing Claims?",
      },
      {
        href: "/solutions/compliance-regulatory",
        label: "See compliance and regulatory support",
      },
    ],
    finalCta: {
      title: "Bring more confidence to claims that matter",
      body:
        "See how PinkSpider helps health and wellness brands review creator claims and disclosures before they publish.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
    disclaimer: REGULATORY_DISCLAIMER,
  },
  {
    slug: "tips-for-onboarding-creators",
    status: "published",
    contentType: "Guide",
    title: "Tips for Onboarding Creators",
    description:
      "Creators want to get it right the first time. When they know which claims work and how to disclose on each platform, your team spends less time on revisions and creators spend less time reshooting.",
    category: "Compliance foundations",
    author: "PinkSpider editorial team",
    publishedAt: onboardingChecklistPublishedOn,
    reviewedAt: onboardingChecklistPublishedOn,
    heroImage: "/onboarding-cohort-hero.jpg",
    seo: {
      title: "Tips for Onboarding Creators | PinkSpider",
      description:
        "A step-by-step checklist for onboarding a new creator cohort: the paperwork, the brief, training before the first post, reviewing it, and the day-30 audit that catches what got skipped.",
    },
    canonicalUrl: `${RESOURCE_SITE_URL}/resources/tips-for-onboarding-creators`,
    readTimeMinutes: 4,
    directAnswer:
      "Before a creator cohort's first post goes live, a brand needs a claims list, a per-platform disclosure rule, a named owner for questions, and a written response plan, plus a signed agreement that spells out disclosure, claims, review, access, fixes, training, and AI-use terms in plain language. Training should run before the first post, a person should review every creator's first post regardless of experience, and gifted or seeded creators need the same rules as paid ones. Thirty days in, pulling five records for three random creators (agreement, brief, training, first-post review, and any fix) tells a program whether it is actually working or just looks like it is.",
    sections: [
      {
        id: "before-you-sign",
        heading: "Before you sign anybody",
        paragraphs: [
          "Four things need to be ready before you email the first creator: a claims list, a disclosure rule per platform, a named owner, and a response ladder. If they are not ready, you are inviting people into a program that does not exist yet.",
        ],
        bullets: [
          "A claims list: what creators are allowed to say about the product, and what they are not, in everyday words rather than legal ones.",
          "A disclosure rule per platform: where the tag goes, which words count, and what to do in a video, written separately for each app creators use.",
          "A named owner: one person whose job is to answer \"can I say this?\" within a day.",
          "A response ladder: what happens when something goes wrong, decided and written down while everyone is calm.",
        ],
      },
      {
        id: "the-paperwork",
        heading: "The paperwork",
        paragraphs: [
          "Everyone signs the same agreement, and the disclosure clause is specific enough to hold someone to it. \"Creator agrees to comply with FTC guidelines\" does not tell the creator what to do and does not prove later that you told them. The agreement should spell out disclosure, claims, review, access, fixes, training, and AI content in plain language, and the signature should be saved with a date somewhere other than one person's email.",
        ],
        bullets: [
          "Disclosure: the exact words to use and where to put them, for each app.",
          "Claims: only approved lines, with no unlisted health, money, or comparative claims.",
          "Review: whether you can see a post before it goes up, and how much warning you will give.",
          "Access: that you will be looking at their public posts about your brand.",
          "Fixes: how many days they have to change or remove a post if asked, and what happens if they do not.",
          "Training: that finishing it is a requirement, not a suggestion.",
          "AI content: that they tell you if AI made or voiced any part of it.",
        ],
      },
      {
        id: "the-brief",
        heading: "The brief",
        paragraphs: [
          "The brief is where most of this actually works, because it is the one document a creator reads more than once. Move the rules to the front and keep them short: a creator can only follow a rule they remember while the camera is rolling. Showing one good post next to one bad post teaches more than a page of rules, and writing the disclosure requirement into the job itself, rather than a separate document, means the rule travels with the work.",
        ],
        bullets: [
          "Say this: three to five approved sentences about the product, written the way a person actually talks.",
          "Never say this: the short list of words that get you in trouble.",
          "Disclose like this: the exact tag or phrase, where to put it, and how long it has to stay on screen.",
          "If you are not sure, ask: the name of the person to ask, right in the brief.",
        ],
      },
      {
        id: "training-before-the-first-post",
        heading: "Training before the first post",
        paragraphs: [
          "Ten minutes of training before anyone posts saves more trouble than any amount of fixing afterwards. Keep it short enough that people finish it, make it easy to open without creating an account, write down who finished and when, and send it again to anyone who just got something wrong.",
        ],
        bullets: [
          "Explain why disclosure exists, in about a minute.",
          "Show where the tag goes on each app they will use, with screenshots.",
          "List the five phrases that cause the most trouble in your category, with a safe version of each.",
          "Say what happens if they get it wrong, so a first warning never comes as a shock.",
          "Tell them who to ask whenever they are not sure.",
        ],
      },
      {
        id: "review-the-first-post",
        heading: "Review the first post, always",
        paragraphs: [
          "A person reads every creator's first post, however experienced they are and however good the brief was. The first post sets the habit for every post after it, and a correction on the first post feels like help, while the same correction on the fortieth feels like a trap. Feedback that names the exact moment and gives the words to use gets fixed within the hour; \"not compliant\" does not.",
        ],
        bullets: [
          "Is there a disclosure at all?",
          "Is it somewhere people will actually see it, before the caption gets cut off?",
          "In a video, is it said out loud or shown on screen long enough to read?",
          "Did they make a claim that is not on the approved list, including in the voiceover?",
          "Did they add something of their own, like a personal health story or a before and after?",
        ],
      },
      {
        id: "the-people-who-get-skipped",
        heading: "The people who get skipped",
        paragraphs: [
          "Gifted creators need the same onboarding as paid ones, or they become the hole in your program: getting something for free counts as a material connection, and those posts have to disclose too. Fans, resellers, and affiliates who signed up through someone else's link cannot be onboarded, so decide ahead of time what you will do when you find one. If an agency finds creators for you, ask them to show you one signed agreement, one brief, and one review record from their last group; if they cannot show you all three, that gap belongs to you.",
        ],
        bullets: [
          "Put the rules in the box: a small printed card with the disclosure rule and the claims to avoid.",
          "Use a landing page for seeding: one link with the short rules and a box they tick to say they read them.",
          "Track who you shipped to: a list of everyone sent product, so you know whose posts to check.",
        ],
      },
      {
        id: "the-day-30-check",
        heading: "The day 30 check",
        paragraphs: [
          "Thirty days after the group starts, pick three creators at random and try to find five things about each one: the signed agreement with its date, the brief they were sent and proof it reached them, their finished training with the date, the review of their first post, and anything you asked them to fix. If you can find all five for all three creators in under fifteen minutes, the onboarding is working. If you cannot, that is usually a filing problem rather than a discipline problem: the work was done, but nobody wrote it down. Pick one place where all of it lives, and make saving things there part of the job.",
        ],
      },
    ],
    primarySources: [FTC_DISCLOSURES_101, FTC_ENDORSEMENT_GUIDES],
    relatedProduct: {
      href: "/affiliate-monitoring",
      label: "Run onboarding inside PinkSpider",
      description:
        "Store every signed agreement, track who finished training, and keep the brief and disclosure terms attached to each creator.",
    },
    relatedResources: [
      {
        href: "/resources/creator-marketing-compliance-guide",
        label: "The Creator Marketing Compliance Guide",
      },
      {
        href: "/resources/who-is-liable-for-influencer-marketing-claims",
        label: "Who's Liable for Influencer Marketing Claims?",
      },
    ],
    finalCta: {
      title: "We keep the records, so you do not have to.",
      body:
        "PinkSpider reviews creator content before it goes live and watches affiliate posts after they go up. The agreement, the brief, the training date, and the first-post review all sit in one place.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
    disclaimer: REGULATORY_DISCLAIMER,
  },
  {
    slug: "monthly-creator-compliance-review",
    status: "published",
    contentType: "Insights",
    title: "The Monthly Creator Compliance Review",
    description:
      "A repeatable monthly routine for reviewing creator content: what to check first, how to sort flags into action, the five numbers to track, and the SOP to run it the same way every time.",
    category: "Insights",
    author: "PinkSpider editorial team",
    publishedAt: "2026-09-01",
    reviewedAt: "2026-09-01",
    heroImage: "/monthly-compliance-review-hero.jpg",
    seo: {
      title: "The Monthly Creator Compliance Review | PinkSpider",
      description:
        "A one-person, one-afternoon routine for reviewing creator content every month: severity order, the four action buckets, the five numbers to track, and a ready-to-run SOP.",
    },
    canonicalUrl: `${RESOURCE_SITE_URL}/resources/monthly-creator-compliance-review`,
    readTimeMinutes: 5,
    directAnswer:
      "A boring monthly habit beats a big annual audit. One owner reviews last month's posts in the first week of the month, checks them in severity order (disease claims first, then income promises, then missing or weak disclosure, then off-brief wording), sorts every flag into take it down, fix it, teach it, or watch it, and sends all outreach in one batch. Tracking five numbers every month, posts published, posts reviewed, flags by severity, flags per hundred posts, and days from flag to fix, shows whether the program is actually improving instead of just staying busy.",
    sections: [
      {
        id: "the-routine",
        heading: "Why monthly beats annual",
        paragraphs: [
          "Problems get caught while the post is still young, so fewer people have seen it and the creator has not built a whole series on the same bad line.",
          "You build a record with dates on it, which is the thing that actually protects you later. And you notice patterns, because the same flag showing up three months running is telling you your brief is wrong, not that your creators are careless.",
        ],
      },
    ],
    primarySources: [FTC_DISCLOSURES_101, FTC_ENDORSEMENT_GUIDES],
    relatedProduct: {
      href: "/affiliate-monitoring",
      label: "Run the monthly review inside PinkSpider",
      description:
        "Pull last month's posts, score them against your claims list and disclosure rules, and keep every flag and fix date in one exportable record.",
    },
    relatedResources: [
      {
        href: "/resources/creator-marketing-compliance-guide",
        label: "The Creator Marketing Compliance Guide",
      },
      {
        href: "/resources/tips-for-onboarding-creators",
        label: "Tips for Onboarding Creators",
      },
      {
        href: "/resources/escalation-playbook",
        label: "Escalation Playbook: Process for Non-Compliant Live Posts",
      },
    ],
    finalCta: {
      title: "Let PinkSpider run the first pass.",
      body:
        "PinkSpider reviews last month's creator content against your claims list and disclosure rules, tracks the five numbers automatically, and keeps every flag, bucket, and fix date in one place.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
  },
  {
    slug: "escalation-playbook",
    status: "published",
    contentType: "Guide",
    title: "Escalation Playbook: Process for Non-Compliant Live Posts",
    description:
      "What to do when a live post is already a problem: how to triage in the first hour, the four-level severity ladder, how to talk to the creator, what to do when there is no contract, and when to stop and call counsel.",
    category: "Compliance foundations",
    author: "PinkSpider editorial team",
    publishedAt: "2026-09-08",
    reviewedAt: "2026-09-08",
    heroImage: "/escalation-playbook-hero.jpg",
    seo: {
      title: "Escalation Playbook: Process for Non-Compliant Live Posts | PinkSpider",
      description:
        "A step-by-step escalation playbook for non-compliant creator posts: triage, a four-level severity ladder, creator conversations, no-contract posts, patterns across creators, and when to call counsel.",
    },
    canonicalUrl: `${RESOURCE_SITE_URL}/resources/escalation-playbook`,
    readTimeMinutes: 5,
    directAnswer:
      "Nobody writes a fair policy in the middle of an incident, so the decisions need to happen before one occurs. Save the evidence first, sort the post into one of four severity levels (red, orange, yellow, green), and let the level decide who gets told and how fast. Message the creator privately with the exact problem line, the reason, and a specific ask. If there is no contract, ask nicely, report it, or invite the person into the program. If the same claim shows up in three or more posts, fix the source instead of chasing each post. The moment a regulator, competitor, or lawyer gets in touch, this playbook stops and counsel takes over.",
    sections: [
      {
        id: "before-the-incident",
        heading: "Read this before you need it",
        paragraphs: [
          "The value of a playbook is not that it is clever. It is that somebody already made the hard calls when nobody was upset, so whoever handles the next incident is following a plan instead of guessing.",
          "This covers the common shapes of trouble, from one creator saying the wrong thing to a letter arriving from a regulator. It is written for the person who runs the program, not for lawyers, and it tells you where to stop and call one.",
        ],
        bullets: [
          "Who decides: name one person who can approve a takedown without a meeting, plus a backup.",
          "Who gets told, and when: write down what counts as bad enough to wake someone up.",
        ],
      },
      {
        id: "the-first-hour",
        heading: "The first hour",
        paragraphs: [
          "Before you contact anybody, save the evidence and answer four questions: what exactly was said, is this ours, how bad is the claim, and how far has it gone. This takes ten minutes and it decides everything after it.",
          "Screenshot the post, the caption, and the profile, and download the video. Creators delete things once a brand reaches out, and a deleted post you did not save is a problem you can no longer describe.",
        ],
      },
      {
        id: "four-levels",
        heading: "Four levels",
        paragraphs: [
          "Every incident gets sorted into one of four levels, red, orange, yellow, or green, and the level decides who gets told, how fast the team moves, and what gets asked for.",
          "When it is unclear which level applies, take the higher one. The cost of treating an orange as a red is one awkward conversation; the cost of the reverse is a non-compliant post sitting up for another month.",
        ],
      },
      {
        id: "the-conversation",
        heading: "Talking to the creator",
        paragraphs: [
          "One private message that names the exact problem and makes the fix easy is the whole job. A good message includes the specific line, why it is a problem in one sentence, exactly what to do, a date, and an offer to help.",
          "Being vague to be nice and leading with the contract are the two most common mistakes. Give the creator the replacement wording rather than a vague request to revise.",
        ],
      },
      {
        id: "resistance",
        heading: "When they say no, or go quiet",
        paragraphs: [
          "Follow up once after two days on a different channel. If it is a red or orange and there is still no answer, pause the creator's commission or code and say why.",
          "If a top affiliate refuses on a red-level issue and the team lets it slide because of the revenue involved, the program stops being a program and becomes a preference.",
        ],
      },
      {
        id: "no-contract",
        heading: "The creator you never signed",
        paragraphs: [
          "Some posts come from people with no contract and no relationship to pause: an old ambassador, a fan, someone who found a discount code online. There are three moves: ask nicely, report the post to the platform, or invite the person into the real program.",
          "Search brand names, product names, and discount codes as part of the monthly review; a code in circulation that was never issued points directly at these posts.",
        ],
      },
      {
        id: "patterns",
        heading: "When it is not one post",
        paragraphs: [
          "If the same bad claim shows up in three or more posts from different creators, stop working the posts one at a time and fix the source: the brief, the talking points, or the brand's own website or ads.",
          "Send one message to every affected creator with the corrected wording, then work the posts in severity order, and tell legal and leadership the same day.",
        ],
      },
      {
        id: "stop-and-call-counsel",
        heading: "When someone official gets in touch",
        paragraphs: [
          "A letter from a regulator, a competitor challenge, or a lawyer's message ends this playbook and starts counsel's. Send it to legal the day it arrives, stop deleting anything, and gather the creator agreement, brief, training record, and review history.",
          "Do not reply alone, contact the creator, post publicly, or promise a date or number before counsel says so.",
        ],
      },
      {
        id: "the-debrief",
        heading: "The debrief",
        paragraphs: [
          "After any red or orange incident, spend twenty minutes answering four questions: how did this get published, how did the team find it, how long was it live, and what single change stops the next one.",
          "Write the answers down and put the one change on somebody's list with a date, then check it next month.",
        ],
      },
    ],
    primarySources: [FTC_DISCLOSURES_101, FTC_ENDORSEMENT_GUIDES],
    relatedProduct: {
      href: "/affiliate-monitoring",
      label: "Catch the red-level post before it publishes",
      description:
        "PinkSpider reviews creator content before it goes live and monitors affiliate posts after they ship, so the worst-case post gets caught at the draft stage.",
    },
    relatedResources: [
      {
        href: "/resources/creator-marketing-compliance-guide",
        label: "The Creator Marketing Compliance Guide",
      },
      {
        href: "/resources/monthly-creator-compliance-review",
        label: "The Monthly Creator Compliance Review",
      },
      {
        href: "/resources/who-is-liable-for-influencer-marketing-claims",
        label: "Who's Liable for Influencer Marketing Claims?",
      },
    ],
    finalCta: {
      title: "The best version of this playbook is the one you never have to open.",
      body:
        "PinkSpider reviews creator content before it publishes and monitors affiliate posts after they ship, so the red-level post gets caught at the draft stage instead of after it is live.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
    disclaimer:
      "General information, not legal advice. When something looks serious, stop reading pages like this one and call your lawyer.",
  },
];

export const PUBLISHED_RESOURCES = RESOURCES.filter(
  (resource): resource is PublishedResource => resource.status === "published",
);

export const RESOURCE_CATEGORIES: {
  value: ResourceCategory;
  label: string;
  description: string;
  contentType: ResourceContentType;
}[] = [
  {
    value: "Compliance foundations",
    label: "Compliance foundations",
    description: "Build a repeatable review and monitoring operating rhythm.",
    contentType: "Guide",
  },
  {
    value: "FTC guidance",
    label: "FTC guidance",
    description: "Translate primary-source disclosure guidance into useful checks.",
    contentType: "Guide",
  },
  {
    value: "Affiliate monitoring",
    label: "Affiliate monitoring",
    description: "Know what to look for after creator and affiliate content goes live.",
    contentType: "Guide",
  },
  {
    value: "Regulatory Library",
    label: "Regulatory Library",
    description:
      "Plain-language summaries of regulatory guidance, tied back to the primary source.",
    contentType: "Regulatory Library",
  },
  {
    value: "Templates & Checklists",
    label: "Templates & Checklists",
    description: "Practical tools your team can put to work directly.",
    contentType: "Templates & Checklists",
  },
  {
    value: "Insights",
    label: "Insights",
    description: "Timely analysis and observations on creator marketing compliance.",
    contentType: "Insights",
  },
  {
    value: "Case Studies",
    label: "Case Studies",
    description: "Approved customer stories about compliance programs in practice.",
    contentType: "Case Study",
  },
];

/**
 * Categories that currently have at least one published entry. The hub and
 * SSR rendering should only surface these -- an empty category is not shown
 * publicly until real content exists for it.
 */
export function getPopulatedResourceCategories(
  resources: PublishedResource[] = PUBLISHED_RESOURCES,
): (typeof RESOURCE_CATEGORIES)[number][] {
  const populated = new Set(resources.map((resource) => resource.category));
  return RESOURCE_CATEGORIES.filter((category) => populated.has(category.value));
}

function wordCount(resource: Resource): number {
  return [
    resource.directAnswer,
    ...resource.sections.flatMap((section) => [
      ...section.paragraphs,
      ...(section.bullets ?? []),
      ...(section.subsections ?? []).flatMap((subsection) => [
        ...subsection.paragraphs,
        ...(subsection.bullets ?? []),
      ]),
      ...(section.example
        ? [
            section.example.before,
            section.example.after,
            section.example.note ?? "",
          ]
        : []),
    ]),
  ]
    .join(" ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

export function validateResources(resources: Resource[] = RESOURCES): string[] {
  const errors: string[] = [];
  const seenSlugs = new Set<string>();
  const seenCanonicals = new Set<string>();

  resources.forEach((resource) => {
    const prefix = resource.slug || "(missing slug)";
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(resource.slug)) {
      errors.push(`${prefix}: slug must be lowercase kebab-case`);
    }
    if (seenSlugs.has(resource.slug)) errors.push(`${prefix}: duplicate slug`);
    seenSlugs.add(resource.slug);

    if (!resource.title.trim()) errors.push(`${prefix}: missing title`);
    if (!resource.description.trim()) errors.push(`${prefix}: missing description`);
    if (!resource.author.trim()) errors.push(`${prefix}: missing author`);
    if (!resource.reviewedAt.trim()) errors.push(`${prefix}: missing reviewed date`);
    if (!resource.heroImage.trim()) errors.push(`${prefix}: missing hero image`);
    if (!resource.directAnswer.trim()) errors.push(`${prefix}: missing direct answer`);
    if (!resource.seo.title.trim() || !resource.seo.description.trim()) {
      errors.push(`${prefix}: missing SEO metadata`);
    }
    if (seenCanonicals.has(resource.canonicalUrl)) {
      errors.push(`${prefix}: duplicate canonical URL`);
    }
    seenCanonicals.add(resource.canonicalUrl);
    if (resource.canonicalUrl !== `${RESOURCE_SITE_URL}/resources/${resource.slug}`) {
      errors.push(`${prefix}: canonical URL does not match slug`);
    }
    if (
      resource.contentType === "Guide" &&
      (resource.sections.length < 3 || wordCount(resource) < 300)
    ) {
      errors.push(`${prefix}: content is too thin`);
    }
    if (resource.primarySources.length === 0) {
      errors.push(`${prefix}: missing primary sources`);
    }
    resource.primarySources.forEach((source) => {
      try {
        const url = new URL(source.url);
        if (url.protocol !== "https:") errors.push(`${prefix}: source URL must use HTTPS`);
      } catch {
        errors.push(`${prefix}: source URL is invalid`);
      }
    });
    if (resource.status === "published" && !resource.publishedAt) {
      errors.push(`${prefix}: published resource is missing published date`);
    }
    if (!resource.relatedProduct.href.startsWith("/")) {
      errors.push(`${prefix}: related product must be an internal link`);
    }
    resource.relatedResources.forEach((link) => {
      if (!link.href.startsWith("/")) {
        errors.push(`${prefix}: related resource must be an internal link`);
      }
    });
    if (!resource.finalCta.href.startsWith("/")) {
      errors.push(`${prefix}: final CTA must be an internal link`);
    }

    if (resource.contentType === "Regulatory Library") {
      if (!resource.regulatoryBody.trim()) {
        errors.push(`${prefix}: regulatory entry is missing the regulatory body`);
      }
      if (!resource.jurisdiction.trim()) {
        errors.push(`${prefix}: regulatory entry is missing the jurisdiction`);
      }
      if (!resource.whatTheSourceSays.trim()) {
        errors.push(`${prefix}: regulatory entry is missing "what the source says"`);
      }
      if (!resource.whatToWatchFor.trim()) {
        errors.push(
          `${prefix}: regulatory entry is missing "what marketing teams should pay attention to"`,
        );
      }
      if (!resource.creatorImplications.trim()) {
        errors.push(`${prefix}: regulatory entry is missing creator/influencer implications`);
      }
      if (resource.operationalReviewQuestions.length === 0) {
        errors.push(`${prefix}: regulatory entry needs at least one operational review question`);
      }
      if (!resource.disclaimer) {
        errors.push(`${prefix}: regulatory entry is missing a disclaimer`);
      }
      try {
        const url = new URL(resource.sourceUrl);
        if (url.protocol !== "https:") {
          errors.push(`${prefix}: regulatory source URL must use HTTPS`);
        }
      } catch {
        errors.push(`${prefix}: regulatory entry has an invalid source URL`);
      }
    }

    if (resource.contentType === "Templates & Checklists") {
      if (!resource.toolSummary.trim()) {
        errors.push(`${prefix}: template entry is missing a tool summary`);
      }
      if (resource.toolItems.length === 0) {
        errors.push(`${prefix}: template entry needs at least one tool item`);
      }
      if (resource.toolFormat === "download") {
        if (!resource.downloadUrl || !resource.downloadUrl.trim()) {
          errors.push(
            `${prefix}: template entry is marked as a download but has no real download link (no gated placeholder downloads)`,
          );
        } else if (
          !resource.downloadUrl.startsWith("/") &&
          !resource.downloadUrl.startsWith("https://")
        ) {
          errors.push(`${prefix}: template download link must be an internal path or HTTPS URL`);
        }
      }
    }
  });

  return errors;
}

export const RESOURCE_VALIDATION_ERRORS = validateResources();
if (RESOURCE_VALIDATION_ERRORS.length > 0) {
  throw new Error(
    `Resource library validation failed:\n${RESOURCE_VALIDATION_ERRORS.join("\n")}`,
  );
}

export function getResourceBySlug(slug: string): PublishedResource | undefined {
  return PUBLISHED_RESOURCES.find((resource) => resource.slug === slug);
}

export function getResourceRoute(path: string): ResourceRoute | undefined {
  if (!path.startsWith("/resources/")) return undefined;
  const slug = path.slice("/resources/".length);
  const resource = getResourceBySlug(slug);
  if (!resource || path !== `/resources/${resource.slug}`) return undefined;

  return {
    path,
    status: 200,
    indexation: "index",
    title: resource.seo.title,
    description: resource.seo.description,
    changefreq: "weekly",
    priority: "0.7",
    content: {
      h1: resource.title,
      paragraphs: [resource.directAnswer],
      links: [
        resource.relatedProduct,
        ...resource.relatedResources,
        resource.finalCta,
      ],
    },
    resource,
  };
}

export const PUBLISHED_RESOURCE_ROUTES: ResourceRoute[] = PUBLISHED_RESOURCES.map(
  (resource) => getResourceRoute(`/resources/${resource.slug}`)!,
);
